import { useState } from 'react';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme.jsx';
import { DEFAULT_AUTHENTICATED_ROUTE } from '../../config/routes.js';

const LoginPage = () => {
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const success = await login(email, password);
    if (success) {
      navigate(from ? `${from.pathname}${from.search}` : DEFAULT_AUTHENTICATED_ROUTE, { replace: true });
    } else {
      setError('Falha no login. Verifique suas credenciais.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center relative overflow-hidden bg-background p-4">
      <div className="fixed top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full bg-primary-container/5 blur-[120px] pointer-events-none"></div>
      <div className="fixed bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full bg-primary-container/5 blur-[100px] pointer-events-none"></div>

      {/* Top bar */}
      <div className="fixed top-4 left-4 right-4 flex justify-between z-20">
        <button
          onClick={() => navigate('/')}
          title="Voltar ao início"
          className="flex items-center gap-2 text-on-surface-variant hover:text-primary transition-colors text-sm"
        >
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
          <span className="hidden sm:inline">Início</span>
        </button>
        <button
          onClick={toggleTheme}
          title={theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          className="p-2 rounded-full text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-all"
        >
          <span className="material-symbols-outlined text-[20px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
        </button>
      </div>

      <main className="relative z-10 w-full max-w-md px-6 py-12 flex flex-col items-center">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center opacity-80">
          <img alt="CatholicGPT Logo" className="h-20 w-20 mx-auto rounded-full bg-white object-contain p-2 shadow-sm mb-6" src="/logo-192.png"/>
          <h1 className="font-headline text-3xl font-medium tracking-tight text-primary-container">CatholicGPT</h1>
        </div>

        <div className="w-full glass-panel rounded-xl shadow-[0_24px_48px_rgba(17,20,23,0.8)] p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-32 h-32 bg-primary/10 blur-3xl rounded-full pointer-events-none"></div>

          <div className="text-center mb-6 relative z-10">
            <h2 className="font-headline text-2xl font-bold tracking-tight text-on-surface mb-2">Entrar</h2>
            {location.state?.reason === 'expired' ? (
              <p className="font-body text-sm text-error leading-relaxed">Sua sessão expirou, entre novamente.</p>
            ) : (
              <p className="font-body text-sm text-on-surface-variant leading-relaxed">Acesse sua conta para continuar.</p>
            )}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-6 relative z-10">
            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider" htmlFor="email">E-mail</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">mail</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-4 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  id="email" name="email" placeholder="seu@email.com" required type="email"
                  autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-2">
              <label className="block font-label text-xs font-medium text-on-surface-variant uppercase tracking-wider" htmlFor="password">Senha</label>
              <div className="relative">
                <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant opacity-60 text-lg">lock</span>
                <input
                  className="w-full bg-surface-container-lowest border-0 border-b border-outline-variant/30 rounded-t-lg pl-10 pr-10 py-3 text-on-surface placeholder:text-on-surface-variant/50 focus:ring-0 focus:border-primary transition-colors font-body"
                  id="password" name="password" placeholder="••••••••" required
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                />
                <button className="absolute inset-y-0 right-0 flex items-center pr-3 text-on-surface-variant opacity-60 hover:text-primary transition-colors" type="button" onClick={() => setShowPassword(!showPassword)}>
                  <span className="material-symbols-outlined text-lg">{showPassword ? 'visibility' : 'visibility_off'}</span>
                </button>
              </div>
            </div>

            <div className="flex justify-end mt-[-8px]">
              <Link className="text-xs text-primary hover:text-primary-container transition-colors font-medium" to="/forgot-password">Esqueci a senha</Link>
            </div>

            {error && <p className="text-error text-sm font-body text-center">{error}</p>}

            <div className="pt-2">
              <button className="w-full bg-primary-container hover:bg-primary text-on-primary font-label font-medium py-3 px-6 rounded-full transition-all duration-300 hover:shadow-[0_0_16px_rgba(212,175,55,0.3)] flex items-center justify-center gap-2 group" type="submit">
                Entrar
                <span className="material-symbols-outlined text-lg transition-transform group-hover:translate-x-1">arrow_forward</span>
              </button>
              <div className="text-center mt-4 text-[11px] text-on-surface-variant/50">
                Ao continuar, você concorda com a nossa{' '}
                <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline transition-colors font-medium">Política de Privacidade</a>.
              </div>
            </div>
          </form>

          <div className="mt-6 pt-6 border-t border-surface-container-low text-center relative z-10">
            <p className="text-sm font-label text-on-surface-variant">
              Ainda não tem uma conta?{' '}
              <Link className="text-primary hover:text-primary-container transition-colors ml-1 font-medium" to="/register">Criar conta</Link>
            </p>
            <div className="mt-3 text-xs text-on-surface-variant/50 space-x-3">
              <a href="/termos" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Termos de Uso</a>
              <span>•</span>
              <a href="/privacidade" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">Política de Privacidade</a>
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-auto py-8 text-center relative z-10 w-full">
        <p className="font-headline italic text-on-surface-variant/60 text-sm tracking-wide">Ad Maiorem Dei Gloriam</p>
      </footer>
    </div>
  );
};

export default LoginPage;
